import type { AIUnifiedEventPayload } from '@atlaskit/editor-common/analytics/types/ai-unified-events';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { AddMarkStep, RemoveMarkStep } from '@atlaskit/editor-prosemirror/transform';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import type { AIDecorationExperiencePluginState } from '../decoration/reducer';
import { ACTIONED_EVENT_DELAY } from './constants';
import { mergeRanges, sumTextCounts, withVisualBlockCounts } from './utils';

export type RetentionOperation = {
	current: AIDecorationExperiencePluginState | undefined;
	finalization?: {
		accepted: AIUnifiedEventPayload;
		discarded: AIUnifiedEventPayload;
		id: string;
	};
	previous: AIDecorationExperiencePluginState | undefined;
	transaction: ReadonlyTransaction;
};

type Range = { from: number; to: number };
type Outcome = {
	accepted: AIUnifiedEventPayload;
	discarded: AIUnifiedEventPayload;
	generatedChars: number;
	ranges: Range[];
	removedChars: number;
	timer?: ReturnType<typeof setTimeout>;
};

/** Owns automatic retention independently of immediate undo/redo action analytics. */
export class AutomaticAcceptanceMonitor {
	private pending = new Map<string, Outcome>();
	private resolved = new Set<string>();
	private activeSessionId: string | undefined;

	constructor(private fire: (payload: AIUnifiedEventPayload) => void) {}

	observe(
		tr: ReadonlyTransaction,
		previous: AIDecorationExperiencePluginState | undefined,
		current: AIDecorationExperiencePluginState | undefined,
	): void {
		const sessionId = current?.nextSessionId;
		if (current?.sessionStarted && sessionId !== this.activeSessionId) {
			// A replaced lifecycle must not be released by the next session's completion.
			if (this.activeSessionId) {
				this.cancel(this.activeSessionId);
			}
			this.activeSessionId = sessionId;
		}

		if (tr.docChanged) {
			const isAI =
				tr.getMeta('isAIStreamingTransformation') ||
				tr.getMeta('isAIFinalStreamingTransformation') ||
				tr.getMeta('appendedTransaction')?.getMeta('isAIStreamingTransformation');
			for (const [id, outcome] of this.pending) {
				const hadRanges = outcome.ranges.length > 0;
				const recovered: Range[] = [];
				for (let i = 0; i < tr.mapping.maps.length; i++) {
					const map = tr.mapping.maps[i];
					const surviving: Range[] = [];
					for (const range of outcome.ranges) {
						let from = range.from;
						// Split at insertions too: user-authored text is not generated output.
						map.forEach((oldStart, oldEnd) => {
							if (oldStart >= range.to || oldEnd <= from) {
								return;
							}
							if (from < oldStart) {
								surviving.push({ from: map.map(from, 1), to: map.map(oldStart, -1) });
							}
							const end = Math.min(oldEnd, range.to);
							const start = Math.max(from, oldStart);
							if (start < end) {
								const mirror = tr.mapping.getMirror(i);
								if (mirror !== undefined && mirror > i) {
									// Collab temporarily rolls back local steps and reapplies them.
									// Mapping through the mirror preserves this output; it was not deleted.
									const remaining = tr.mapping.slice(i);
									recovered.push({ from: remaining.map(start, 1), to: remaining.map(end, -1) });
								} else if (!(isAI && id === this.activeSessionId)) {
									outcome.removedChars += sumTextCounts(tr.docs[i], [
										{ from: start, to: end },
									]).chars;
								}
							}
							from = Math.max(from, oldEnd);
						});
						if (from < range.to) {
							surviving.push({ from: map.map(from, 1), to: map.map(range.to, -1) });
						}
					}
					outcome.ranges = surviving.filter((range) => range.from < range.to);
				}
				outcome.ranges = mergeRanges(
					[...outcome.ranges, ...recovered].filter((range) => range.from < range.to),
				);
				if (
					!(isAI && id === this.activeSessionId) &&
					((hadRanges && !outcome.ranges.length) ||
						(outcome.removedChars > 0 && outcome.removedChars >= outcome.generatedChars / 2))
				) {
					this.cancel(id);
				}
			}
		}

		// Closing an experience between regenerated streams can clear sessionStarted
		// while the canonical session is still active.
		if (current?.sessionEnded && (previous?.sessionStarted || this.activeSessionId)) {
			// The normal endAISession command clears the IDs during END_SESSION.
			const endedId = previous?.nextSessionId ?? this.activeSessionId;
			if (endedId) {
				const endSessionData = tr.getMeta(aiExperienceDecorationPluginKey)?.data;
				if (endSessionData?.type === 'finished') {
					for (const outcome of this.pending.values()) {
						outcome.accepted = withVisualBlockCounts(
							outcome.accepted,
							endSessionData.singleInstrumentationId,
							endSessionData.visualBlockCounts,
						);
					}
					this.arm(endedId);
				} else {
					// Cancel pending acceptance on abort/error, including finalized partial output.
					this.cancel(endedId);
				}
			}
			this.activeSessionId = undefined;
		}
	}

	finalize(
		id: string,
		tr: ReadonlyTransaction,
		doc: PMNode,
		accepted: AIUnifiedEventPayload,
		discarded: AIUnifiedEventPayload,
	): void {
		if (this.resolved.has(id)) {
			return;
		}
		const previous = this.pending.get(id);
		clearTimeout(previous?.timer);
		// Each step's new coordinates predate the remaining steps in the transaction.
		const newRanges: Range[] = [];
		tr.mapping.maps.forEach((map, index) => {
			const remaining = tr.mapping.slice(index + 1);
			map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
				newRanges.push({ from: remaining.map(newStart, 1), to: remaining.map(newEnd, -1) });
			});
			const step = tr.steps[index];
			if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) {
				newRanges.push({ from: remaining.map(step.from, 1), to: remaining.map(step.to, -1) });
			}
		});
		// Keep existing qualification for formatting, structural changes, and identical rewrites.
		const ranges = mergeRanges([
			...(previous?.ranges ?? []),
			...newRanges.filter((range) => range.from < range.to),
		]);
		const removedChars = previous?.removedChars ?? 0;
		this.pending.set(id, {
			accepted: previous?.accepted ?? accepted,
			discarded: previous?.discarded ?? discarded,
			ranges,
			generatedChars: sumTextCounts(doc, ranges).chars + removedChars,
			removedChars,
		});
		if (id !== this.activeSessionId) {
			this.arm(id);
		}
	}

	private arm(id: string): void {
		const outcome = this.pending.get(id);
		if (!outcome || this.resolved.has(id)) {
			return;
		}
		clearTimeout(outcome.timer);
		outcome.timer = setTimeout(() => {
			this.pending.delete(id);
			this.resolved.add(id);
			this.fire(outcome.accepted);
		}, ACTIONED_EVENT_DELAY);
	}

	private cancel(id: string): void {
		const outcome = this.pending.get(id);
		clearTimeout(outcome?.timer);
		this.pending.delete(id);
		this.resolved.add(id);
		if (outcome && isExperimentEnabled('platform_editor_discarded_unified_analytic_events')) {
			this.fire(outcome.discarded);
		}
	}
}
